import React, { useCallback, useMemo } from "react"
import type { AjvError, Field } from "@rjsf/core"
import { findExamples } from "../../../Helpers/Schema/Schema"
import { useUrlSignerFromContext } from "../../../Hooks/useUrlSigner"
import { ErrorBoundary } from "../ErrorBoundary/ErrorBoundary"
import { SchemaForm } from "../Lazy/SchemaForm"
import type {
    PrelabelResponse,
    SchemaFormData,
    SchemaFormRefProps
} from "resha"
import { useDispatch } from "react-redux"
import { MainThunkDispatch } from "../../../Reducers"
import { uploadData } from "../../../Actions/API/Data/DataActions"
import { appendTimestampToFileName } from "../../../Helpers/Functions/random"
import { ClassificationParamsField } from "../SchemaForm/Fields/ClassificationParamsField"

export type InputFormRenderProps = SchemaFormData & {
    id?: string
    renderExamples?: boolean
    className?: string
    testId?: string
    shouldRenderFieldInPanel?: boolean
    prelabels?: PrelabelResponse
    liveValidate?: boolean
    skipMissingDataField?: boolean
    documentMaxHeight?: string
    enableImageTools?: boolean
    additionalFields?: {
        [name: string]: Field
    }
    onChange?: (value: unknown, errors: AjvError[]) => void
    onSubmit?: (value: unknown) => void
    onError?: (errors: AjvError[]) => void
    onExtraError?: (errors: AjvError[]) => void
    urlSigner?: (url: string) => Promise<string>
}

const UnsafeInputFormRender = React.forwardRef<
    SchemaFormRefProps,
    InputFormRenderProps
>(
    (
        {
            id,
            testId,
            formData,
            schema,
            uiSchema,
            renderExamples,
            className,
            shouldRenderFieldInPanel,
            liveValidate = false,
            prelabels,
            skipMissingDataField = false,
            documentMaxHeight,
            enableImageTools = true,
            additionalFields,
            onChange,
            onSubmit,
            onError,
            onExtraError,
            urlSigner
        },
        ref
    ) => {
        const urlSignerFromContext = useUrlSignerFromContext()
        const dispatch = useDispatch<MainThunkDispatch>()
        const handleUploadBlob = useCallback(
            (blob: Blob, name?: string) =>
                dispatch(
                    uploadData(blob, appendTimestampToFileName(name ?? ""))
                ),
            [dispatch]
        )
        const examples = useMemo(() => {
            if (renderExamples) {
                return findExamples(schema, true)[0]
            }

            return undefined
        }, [renderExamples, schema])

        return (
            <div data-testid={testId} className="w-full">
                <SchemaForm
                    id={id}
                    ref={ref}
                    className={className}
                    schema={schema}
                    uiSchema={uiSchema}
                    formData={examples ?? formData}
                    prelabels={prelabels}
                    urlSigner={urlSigner ?? urlSignerFromContext}
                    blobUploader={handleUploadBlob}
                    documentMaxHeight={documentMaxHeight}
                    shouldRenderFieldInPanel={shouldRenderFieldInPanel}
                    liveValidate={liveValidate}
                    skipMissingDataField={skipMissingDataField}
                    enableImageTools={enableImageTools}
                    additionalFields={{
                        "https://widgets.super.ai/classification-params@1":
                            ClassificationParamsField,
                        ...additionalFields
                    }}
                    onChange={onChange}
                    onError={onError}
                    onSubmit={onSubmit}
                    onExtraError={onExtraError}
                    extraError={true}
                />
            </div>
        )
    }
)

export const InputFormRender = React.forwardRef<
    SchemaFormRefProps,
    InputFormRenderProps
>((props, ref) => {
    return (
        <ErrorBoundary message="Could not render data.">
            <UnsafeInputFormRender ref={ref} {...props} />
        </ErrorBoundary>
    )
})
